import { Outlet, useLocation, useNavigate, useRouterState } from "@tanstack/solid-router";
import { createEffect, createMemo, createSignal, on, onCleanup, onMount, Show } from "solid-js";
import { useSession } from "@/lib/auth";
import { appUndoStack } from "@/lib/undo";
import type { MessageDescriptor } from "@lingui/core";
import { AccountSwitcherHost } from "@/features/auth/account-switcher-host";

import { appState } from "@fleet/ui";
import { useTheme } from "@/providers/theme-provider";
import { useI18n } from "@/providers/i18n-provider";
import { useSearchPalette } from "@/features/search/hooks/use-search-palette";

// Layout specific components and providers
import { AppBackground } from "@/components/app-background";
import { AppHeader } from "@/components/app-header";
import { OfflineBanner } from "@/components/offline-banner";
import { CommandPalette } from "@/features/search/components/command-palette";

// Layout module parts
import { useInteractionFlag } from "@fleet/ui";
import { SwipeIndicators } from "./swipe-indicators";
import { AppLayoutSidebar } from "./app-layout-sidebar";
import { AppLayoutDialogs } from "./app-layout-dialogs";
import { usePageSwipeGestures } from "../hooks/use-page-swipe-gestures";
import { useAppDialogs } from "../hooks/use-app-dialogs";
import { useAppThemeState } from "../hooks/use-app-theme-state";
import { useAppShortcuts } from "../hooks/use-app-shortcuts";
import { getOrderedPagePaths, getPageNameForPath } from "../helpers";

function getAdjacentPageNamesHelper(
  paths: string[],
  currentIndex: number,
  t: (msg: MessageDescriptor) => string,
) {
  if (paths.length === 0 || currentIndex === -1) {
    return { prev: "", next: "" };
  }
  const nextIndex = (currentIndex + 1) % paths.length;
  const prevIndex = (currentIndex - 1 + paths.length) % paths.length;

  return {
    prev: getPageNameForPath(paths[prevIndex] ?? "", t),
    next: getPageNameForPath(paths[nextIndex] ?? "", t),
  };
}

function getNextPath(isNext: boolean, paths: string[], currentIndex: number): string | undefined {
  if (paths.length === 0 || currentIndex === -1) {
    return undefined;
  }
  const nextIndex = isNext
    ? (currentIndex + 1) % paths.length
    : (currentIndex - 1 + paths.length) % paths.length;
  return paths[nextIndex];
}

function useGlobalEventListeners(
  isSidebarOpen: () => boolean,
  setIsSidebarOpen: (open: boolean) => void,
) {
  // Keyboard-vs-pointer activity tracking (and the `body.keyboard-navigation`
  // class) is owned by <InteractionProvider>; only the sidebar-specific Escape
  // handler remains here.
  onMount(() => {
    const handleEscapeForSidebar = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isSidebarOpen()) {
        setIsSidebarOpen(false);
      }
    };

    globalThis.addEventListener("keydown", handleEscapeForSidebar);

    onCleanup(() => {
      globalThis.removeEventListener("keydown", handleEscapeForSidebar);
    });
  });
}

export function AppLayoutContent() {
  onMount(() => {
    // Notify the InitialSyncOverlay that we are mounted and ready
    appState.markAsReady();
  });

  const navigate = useNavigate();
  const searchPalette = useSearchPalette();
  const { t } = useI18n();

  const handleCloseSearch = () => {
    searchPalette.close();
  };

  const location = useLocation();
  const routerState = useRouterState();

  // ACTIVITY-CORE L1: undo stack is per-account. When changing active
  // account (multi-account switch) it is emptied to avoid carrying over foreign actions.
  const { userId } = useSession();
  createEffect(
    on(
      userId,
      () => {
        appUndoStack.clear();
      },
      { defer: true },
    ),
  );

  // Track the target path during fast keyboard navigation to avoid lag
  const [navigationTargetPath, setNavigationTargetPath] = createSignal<string | null>(null);

  createEffect(() => {
    const currentPath = location().pathname;
    const target = navigationTargetPath();
    if (currentPath === target || routerState().status !== "pending") {
      setNavigationTargetPath(null);
    }
  });

  const orderedPagePaths = createMemo(() => getOrderedPagePaths());

  // Key for the per-route entrance animation. Uses the pathname only (search
  // params excluded) so the slide-up+fade replays when the user lands on a new
  // page, but in-page filtering/sorting (which only mutates search) does not
  // re-trigger it.
  const routeAnimationKey = createMemo(() => location().pathname);

  const currentPathIndex = () => {
    const current = navigationTargetPath() ?? location().pathname;
    return orderedPagePaths().indexOf(current);
  };

  const getAdjacentPageNames = () =>
    getAdjacentPageNamesHelper(orderedPagePaths(), currentPathIndex(), t);

  const navigatePage = (isNext: boolean) => {
    const targetPath = getNextPath(isNext, orderedPagePaths(), currentPathIndex());
    if (targetPath) {
      setNavigationTargetPath(targetPath);
      void navigate({ to: targetPath });
    }
  };

  const {
    handlers: pageSwipeHandlers,
    swipeListTx,
    isSwipingList,
    swipeListTy,
    isSwipingListY,
    swipeListAnimating,
    swipeListYAnimating,
    LIST_SWIPE_INDICATOR_TRIGGER_PX,
    LIST_SWIPE_COMMIT_THRESHOLD_PX,
  } = usePageSwipeGestures({
    location,
    navigate,
    orderedListPaths: orderedPagePaths,
    currentPathIndex,
    navigateList: navigatePage,
  });

  const isNavigating = () => routerState().status === "pending";

  const theme = useTheme();

  const {
    isSidebarOpen,
    setIsSidebarOpen,
    shortcutsOpen,
    setShortcutsOpen,
    // `location` is passed by reference (the accessor itself), not read here;
    // `useAppDialogs` tracks it internally. The rule can't see through the
    // positional arg, so the read-outside-tracked-scope warning is a false positive.
    // oxlint-disable-next-line solid/reactivity
  } = useAppDialogs(location);

  useAppShortcuts({
    searchPalette,
    navigate,
    theme,
    setShortcutsOpen,
    setIsSidebarOpen,
    navigatePage,
  });

  // ── Keyboard-navigation visual indicator + touch gestures
  useGlobalEventListeners(isSidebarOpen, setIsSidebarOpen);

  // With the nav drawer open, the horizontal gesture belongs to it (drag to
  // dismiss). The page swipe listens on the window and would otherwise fire
  // underneath it, so ownership is announced in the interaction context rather
  // than re-derived from the DOM by each gesture.
  useInteractionFlag({ "nav-drawer.open": isSidebarOpen });

  const { activeBlur, activeBackground, glows } = useAppThemeState({ theme });

  return (
    <>
      <AppBackground
        class="flex h-dvh text-foreground overflow-hidden"
        isBlur={activeBlur()}
        background={activeBackground()}
        patternType={theme.tokens()?.patternType}
        patternOpacity={theme.tokens()?.patternOpacity}
        {...glows()}
      >
        <SwipeIndicators
          isSwipingList={isSwipingList}
          swipeListTx={swipeListTx}
          isSwipingListY={isSwipingListY}
          swipeListTy={swipeListTy}
          LIST_SWIPE_INDICATOR_TRIGGER_PX={LIST_SWIPE_INDICATOR_TRIGGER_PX}
          LIST_SWIPE_COMMIT_THRESHOLD_PX={LIST_SWIPE_COMMIT_THRESHOLD_PX}
          location={location}
          getAdjacentListNames={getAdjacentPageNames}
          t={t}
        />

        <AppLayoutSidebar isOpen={isSidebarOpen} setOpen={setIsSidebarOpen} />

        {/* ── Main content ───────────────────────────────────────── */}
        <main
          class="flex flex-1 flex-col overflow-hidden bg-transparent pt-[var(--safe-area-inset-top)] pr-[var(--safe-area-inset-right)] pb-[var(--safe-area-inset-bottom)]"
          {...pageSwipeHandlers}
        >
          <AppHeader onMenuClick={() => setIsSidebarOpen(true)} isNavigating={isNavigating} />
          <OfflineBanner />
          <div
            class="flex flex-1 flex-col min-h-0 overflow-hidden pr-[var(--safe-area-inset-right)] pl-[var(--safe-area-inset-left)]"
            style={{
              transform: `translate3d(${swipeListTx()}px, ${swipeListTy()}px, 0)`,
              transition:
                swipeListAnimating() || swipeListYAnimating()
                  ? "transform 240ms cubic-bezier(0.32, 0.72, 0, 1)"
                  : "none",
              "will-change":
                isSwipingList() || isSwipingListY() || swipeListAnimating() || swipeListYAnimating()
                  ? "transform"
                  : "auto",
            }}
          >
            {/* Per-route entrance animation (slide-up + fade). `keyed` on the
                route path forces a fresh DOM node on navigation so the CSS
                animation re-runs once per page, but NOT on unrelated reactive
                updates within the same page. Lives on its own wrapper (never the
                swipe-transform element) to avoid transform conflicts. GPU-only
                (transform/opacity); honoured by the global
                prefers-reduced-motion guard in global.css. */}
            <Show when={routeAnimationKey()} keyed>
              {(key) => (
                <div
                  data-route-key={key}
                  class="flex flex-1 flex-col min-h-0 h-full animate-slide-in-up will-change-[transform,opacity]"
                >
                  <Outlet />
                </div>
              )}
            </Show>
          </div>
        </main>

        <AppLayoutDialogs shortcutsOpen={shortcutsOpen} setShortcutsOpen={setShortcutsOpen} />
      </AppBackground>

      {/* Command Palette — rendered OUTSIDE AppBackground to avoid blur compositor flash */}
      <CommandPalette isOpen={searchPalette.isOpen()} onClose={handleCloseSearch} />

      {/* Account switcher overlay — mounted ONCE at the layout level (URL-driven
          via ?account=switch); the per-sidebar triggers only flip the URL flag. */}
      <AccountSwitcherHost />
    </>
  );
}
